import { stripHtmlTags } from 'utils/misc'

const NON_ANSWER_SURVEY_TYPES = new Set(['introduction', 'prompt', 'pdf'])

/** Displayable choice value for answer columns (never fall back to choice id first). */
export const getChoiceAnswerValue = choice => {
  const rawValue = choice?.value
  if (rawValue != null && String(rawValue).trim() !== '') {
    return String(rawValue)
  }
  const label = stripHtmlTags(choice?.label || '').trim()
  if (label) return label
  return choice?.id != null ? String(choice.id) : ''
}

/**
 * Selected choice ids/values for multi-select answers. Stored answers reach us as a
 * JSON array, a real array, or a comma separated string depending on the source.
 */
export const parseChoiceValueList = value => {
  if (value == null || value === '') return []
  if (Array.isArray(value)) return value.map(String)
  const raw = String(value).trim()
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(String)
    if (parsed != null && typeof parsed !== 'object') return [String(parsed)]
  } catch (error) {
    // Not JSON — fall through to delimiter parsing.
  }
  return raw
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

/**
 * Survey definition metadata (type, choices, validation) merged onto a stored answer.
 * The parse DB keeps only answer values, so the instrument survey decides how the
 * answer is edited.
 */
export const withSurveyQuestionMeta = (answer, meta) => {
  if (!meta) return answer
  return {
    ...answer,
    questionNumber: answer.questionNumber ?? meta.questionNumber,
    questionLabel: meta.questionLabel || answer.questionLabel,
    questionTitle: meta.questionTitle || answer.questionTitle,
    questionHtml: meta.questionHtml || answer.questionHtml,
    questionType: meta.questionType || answer.questionType,
    choices: meta.choices || [],
    attributes: meta.attributes || {},
    inputValidation: meta.inputValidation || {},
    isSurveyAnswer: true,
  }
}

export const formatSurveyQuestions = (survey = {}) => {
  const questions = survey.questions || {}
  const orderedQuestionIds = (survey.order || []).reduce(
    (result, page) => result.concat(Array.isArray(page) ? page : []),
    [],
  )

  return orderedQuestionIds
    .map(questionId => ({ questionId: String(questionId), question: questions[questionId] }))
    .filter(({ question }) => question && !NON_ANSWER_SURVEY_TYPES.has(question.type))
    .map(({ questionId, question }, index) => {
      const choices = question.choices || {}
      const choiceOrder =
        Array.isArray(question.choices_order) && question.choices_order.length
          ? question.choices_order
          : Object.keys(choices)
      const questionHtml = question.label || questionId
      const questionTitle = stripHtmlTags(questionHtml).trim() || questionId

      return {
        questionId,
        questionNumber: index + 1,
        questionLabel: questionTitle,
        questionTitle,
        questionHtml,
        questionType: question.type || 'text',
        choices: choiceOrder
          .filter(choiceId => choices[choiceId])
          .map(choiceId => {
            const choice = choices[choiceId] || {}
            return {
              id: String(choiceId),
              label: choice.label || String(choice.value ?? choiceId),
              value: getChoiceAnswerValue({ id: choiceId, ...choice }),
            }
          }),
        attributes: question.attributes || {},
        inputValidation: question.input_validation || {},
        originalAnswerId: null,
        answerId: null,
        originalAnswer: '',
        correctedAnswer: '',
        isSurveyAnswer: true,
      }
    })
}

/** questionId → survey question metadata, for merging onto stored answers. */
export const buildSurveyQuestionMap = (survey = {}) => {
  const byId = {}
  formatSurveyQuestions(survey).forEach(question => {
    byId[question.questionId] = question
  })
  return byId
}
